/**
 * Container Stakeholders Component
 *
 * Displays all parties involved in a shipment, including:
 * - Known parties from the container model
 * - AI-extracted parties from documents
 * - Shipment steps with responsible parties
 * - Quick reference contacts
 */

import { useContainerStakeholders } from '@/hooks/api/use-container-stakeholders';
import type { StakeholderInfo, ContactInfo } from '@/hooks/api/use-container-stakeholders';
import { useTranslation } from 'react-i18next';
import {
    Building02,
    User01,
    Mail01,
    Phone,
    MarkerPin01,
    AlertCircle,
    File06,
} from '@untitledui/icons';
import { Badge } from '@/components/base/badges/badges';

interface ContainerStakeholdersProps {
    containerId: string | undefined;
    containerStatus?: string;
    className?: string;
}

// Role display names and colors
const getRoleConfig = (role: string) => {
    const configs: Record<string, { label: string; color: string }> = {
        shipper: { label: 'Shipper', color: 'brand' },
        consignee: { label: 'Consignee', color: 'success' },
        freight_forwarder: { label: 'Freight Forwarder', color: 'warning' },
        notify_party: { label: 'Notify Party', color: 'gray' },
        carrier: { label: 'Carrier', color: 'brand' },
        customs_broker: { label: 'Customs Broker', color: 'warning' },
        insurance_provider: { label: 'Insurance', color: 'gray' },
        inspection_agency: { label: 'Inspection Agency', color: 'gray' },
        bank: { label: 'Bank', color: 'gray' },
        advising_bank: { label: 'Advising Bank', color: 'gray' },
        exporter: { label: 'Exporter', color: 'brand' },
        importer: { label: 'Importer', color: 'success' },
        origin_agent: { label: 'Origin Agent', color: 'warning' },
        destination_agent: { label: 'Destination Agent', color: 'warning' },
        last_mile_carrier: { label: 'Last Mile Carrier', color: 'gray' },
        delivery_agent: { label: 'Delivery Agent', color: 'gray' },
    };
    return configs[role] || { label: role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()), color: 'gray' };
};

// Stakeholder card component
const StakeholderCard = ({ stakeholder }: { stakeholder: StakeholderInfo }) => {
    const config = getRoleConfig(stakeholder.role);
    const isFromDocument = stakeholder.source.startsWith('document:');

    return (
        <div className="rounded-lg bg-secondary/30 p-4 ring-1 ring-secondary">
            <div className="flex items-start justify-between mb-2">
                <Badge color={config.color as 'brand' | 'success' | 'warning' | 'gray' | 'error'} size="sm">
                    {config.label}
                </Badge>
                {isFromDocument && (
                    <div className="flex items-center gap-1 text-xs text-tertiary" title={`Extracted from ${stakeholder.source.replace('document:', '')}`}>
                        <File06 className="h-3 w-3" />
                        <span>Doc</span>
                    </div>
                )}
            </div>

            <div className="space-y-2">
                {stakeholder.company_name && (
                    <div className="flex items-center gap-2">
                        <Building02 className="h-4 w-4 text-tertiary shrink-0" />
                        <span className="text-sm font-medium text-primary truncate">{stakeholder.company_name}</span>
                    </div>
                )}
                {stakeholder.contact_name && (
                    <div className="flex items-center gap-2">
                        <User01 className="h-4 w-4 text-tertiary shrink-0" />
                        <span className="text-sm text-secondary truncate">{stakeholder.contact_name}</span>
                    </div>
                )}
                {stakeholder.email && (
                    <div className="flex items-center gap-2">
                        <Mail01 className="h-4 w-4 text-tertiary shrink-0" />
                        <a href={`mailto:${stakeholder.email}`} className="text-sm text-brand-600 hover:underline truncate">
                            {stakeholder.email}
                        </a>
                    </div>
                )}
                {stakeholder.phone && (
                    <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-tertiary shrink-0" />
                        <a href={`tel:${stakeholder.phone}`} className="text-sm text-brand-600 hover:underline">
                            {stakeholder.phone}
                        </a>
                    </div>
                )}
                {stakeholder.address && (
                    <div className="flex items-start gap-2">
                        <MarkerPin01 className="h-4 w-4 text-tertiary shrink-0 mt-0.5" />
                        <span className="text-xs text-tertiary line-clamp-2">{stakeholder.address}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

// Quick contact item
const QuickContactItem = ({ contact }: { contact: ContactInfo }) => (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-secondary/30 transition-colors">
        <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
                <User01 className="h-4 w-4 text-brand-600" />
            </div>
            <div className="min-w-0">
                <p className="text-sm font-medium text-primary truncate">{contact.name}</p>
                <p className="text-xs text-tertiary">{contact.role}</p>
            </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
            {contact.phone && (
                <a href={`tel:${contact.phone}`} className="p-1.5 rounded-lg hover:bg-secondary transition-colors" title={contact.phone}>
                    <Phone className="h-4 w-4 text-tertiary hover:text-primary" />
                </a>
            )}
            {contact.email && (
                <a href={`mailto:${contact.email}`} className="p-1.5 rounded-lg hover:bg-secondary transition-colors" title={contact.email}>
                    <Mail01 className="h-4 w-4 text-tertiary hover:text-primary" />
                </a>
            )}
        </div>
    </div>
);

export function ContainerStakeholders({
    containerId,
    containerStatus: _containerStatus,
    className = '',
}: ContainerStakeholdersProps) {
    const { t } = useTranslation('pages:containers');
    const { data, isLoading, error } = useContainerStakeholders(containerId);

    if (isLoading) {
        return (
            <div className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary ${className}`}>
                <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.stakeholders', 'Companies Involved')}</h2>
                <div className="flex items-center justify-center py-12">
                    <p className="text-tertiary">{t('detail.loading', 'Loading...')}</p>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary ${className}`}>
                <h2 className="text-lg font-semibold text-primary mb-4">{t('detail.info.stakeholders', 'Companies Involved')}</h2>
                <div className="flex items-center justify-center py-12 text-error-600">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    <span>Failed to load stakeholders</span>
                </div>
            </div>
        );
    }

    const { stakeholders, useful_contacts } = data;

    // Group stakeholders by category
    const primaryStakeholders = stakeholders.filter(s =>
        ['shipper', 'consignee', 'freight_forwarder', 'carrier'].includes(s.role)
    );
    const secondaryStakeholders = stakeholders.filter(s =>
        !['shipper', 'consignee', 'freight_forwarder', 'carrier'].includes(s.role)
    );

    return (
        <div className={`rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary ${className}`}>
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-primary">{t('detail.info.stakeholders', 'Companies Involved')}</h2>
                <span className="text-xs text-tertiary">
                    {stakeholders.length} {stakeholders.length === 1 ? 'party' : 'parties'}
                </span>
            </div>

            {stakeholders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Building02 className="h-12 w-12 text-tertiary mb-3" />
                    <p className="text-sm text-tertiary">No stakeholders found</p>
                    <p className="text-xs text-tertiary mt-1">
                        Add companies to the shipment or upload documents with party information
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Stakeholders Grid */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Primary Stakeholders */}
                        {primaryStakeholders.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-secondary uppercase tracking-wide mb-3">
                                    {t('detail.info.sections.primaryParties', 'Primary Parties')}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {primaryStakeholders.map((stakeholder, idx) => (
                                        <StakeholderCard key={`${stakeholder.role}-${idx}`} stakeholder={stakeholder} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Secondary Stakeholders (from documents) */}
                        {secondaryStakeholders.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-secondary uppercase tracking-wide mb-3">
                                    {t('detail.info.sections.otherParties', 'Other Parties (from documents)')}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {secondaryStakeholders.map((stakeholder, idx) => (
                                        <StakeholderCard key={`${stakeholder.role}-${idx}`} stakeholder={stakeholder} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right Column - Contacts only (Steps now in expandable timeline above) */}
                    <div className="space-y-6">
                        {/* Quick Contacts */}
                        {useful_contacts.length > 0 && (
                            <div>
                                <h3 className="text-sm font-medium text-secondary uppercase tracking-wide mb-3">
                                    {t('detail.info.sections.quickContacts', 'Quick Contacts')}
                                </h3>
                                <div className="rounded-lg ring-1 ring-secondary divide-y divide-secondary">
                                    {useful_contacts.map((contact, idx) => (
                                        <QuickContactItem key={idx} contact={contact} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
